import {
  AppicationErrorReason,
  ApplicationError,
  CustomerCtx,
  LeadCtx
} from "@closerplatform/spinner-openapi";
import { staticConfig } from "@fidget/common/config/static-config";
import {
  AuthorizationParameters,
  ReinitParameters
} from "@fidget/common/models/AuthorizationParameters";
import { Credentials } from "@fidget/common/models/credentials";
import { SafeLocalStorageService } from "@fidget/common/services/safeLocalStorageService";
import { logger, noop } from "@fidget/common/utils";
import { InitialContext } from "./initialContextService";
import { ForeSpinnerService } from "./spinnerService";
import { extractApplicationError } from "@fidget/common/utils/errorHelpers";
import { HostWindowRequests } from "./hostWindowRequests";
import { fileService } from "@fidget/common/services/files/fileService";
export enum AuthErrors {
  OAuthError = "oauth_error",
  GeneralAuthError = "general_auth_error",
  RateLimitExceeded = "rate_limit_exceeded",
}
export class AuthService {
  private readonly safeLocalStorage = SafeLocalStorageService.getInstance();
  private readonly spinnerService = ForeSpinnerService.getInstance();
  private readonly orgId: string;
  private readonly hostWindowRequests: HostWindowRequests;
  private readonly localStorageKey: string;

  constructor(orgId: string, hostWindowRequests: HostWindowRequests) {
    this.orgId = orgId;
    this.hostWindowRequests = hostWindowRequests;
    this.localStorageKey = getLocalStorageCredentialsKey(orgId);
    fileService.setSpinnerService(this.spinnerService);
    this.detectStoredCredentialChange();
  }

  private detectStoredCredentialChange() {
    let previous: any;
    const stringify = (credentials: any) => credentials?.id ? `[id=${credentials.id}]` : "[null]";
    if (this.safeLocalStorage.isSupported()) {
      setInterval(() => {
        let current = deserializeStoredCredentials(this.safeLocalStorage.getItem(this.localStorageKey) || "{}");
        if (current?.id !== previous?.id) {
          logger.info(`Closer auth: stored credentials changed from ${stringify(previous)} to ${stringify(current)}`);
          previous = current;
        }
      }, 1000);
    }
  }

  /*
  first init if oauthDisabled, then stored
  */
  async authorize(
    authorizationParameters: AuthorizationParameters,
    getOAuthCode: () => Promise<string>,
    forceNewUser?: boolean,
    signUpContext?: InitialContext
  ): Promise<LeadCtx> {
    const { reinitParameters, oauthEnabled } = authorizationParameters;
    logger.debug(
      `Closer auth: starting authorization, forceNewUser: ${forceNewUser}, reinitParameters: ${reinitParameters}, oauthEnabled: ${oauthEnabled}`
    );
    const storedCredentials = await this.getStoredCredentials();

    const session = await this.aquireCurrentSession(
      storedCredentials,
      authorizationParameters,
      getOAuthCode,
      forceNewUser,
      signUpContext
    );

    await this.handleReinit(session, {
      enabled: !!authorizationParameters.reinitParameters?.enabled,
      credentials: storedCredentials?.apiKey ? storedCredentials : undefined,
    });

    logger.debug("Closer auth: successfully acquired session");
    return session;
  }

  async aquireCurrentSession(
    storedCredentials: Credentials | undefined,
    authorizationParameters: AuthorizationParameters,
    getOAuthCode: () => Promise<string>,
    forceNewUser?: boolean,
    signUpContext?: InitialContext
  ): Promise<LeadCtx> {
    const initApiKey = !!authorizationParameters.oauthEnabled
      ? undefined
      : authorizationParameters.apiKey;
    const apiKey = initApiKey || storedCredentials?.apiKey;
    if (apiKey !== undefined && !forceNewUser && !authorizationParameters.reinitParameters?.enabled) {
      this.spinnerService.setApiKey(apiKey);
      fileService.setApiKey(apiKey);
      logger.debug("Closer auth: acquiring existing session");
      const leadCtx = await this.spinnerService.getOwnLeadCtx(this.orgId).catch(async (e) => {
        const responseBody: ApplicationError = await extractApplicationError(e as Response);
        if (
          !authorizationParameters.oauthEnabled &&
          responseBody.error === AppicationErrorReason.SessionExpired
        ) {
          logger.info("Closer auth: saved session is expired, signing up");
          return this.signUpGuest(signUpContext);
        } else {
          logger.error({ message: "Couldn't aquire own lead context", error: e });
          return Promise.reject(AuthErrors.GeneralAuthError);
        }
      });
      this.saveGuestSession(leadCtx);
      return leadCtx;
    }
    if (!!authorizationParameters.oauthEnabled) {
      logger.debug("Closer auth: signing up with OAuth");
      const loginHintToken = await getOAuthCode().catch((e) => {
        logger.error({ message: "Closer auth: getting OAuth code failed", error: e });
        throw e;
      });
      return this.signInWithOAuth(loginHintToken).catch((e) => {
        logger.error({ message: "Closer auth: couldn't sign up with OAuth", error: e });
        return Promise.reject(AuthErrors.OAuthError);
      });
    }
    return this.signUpGuest(signUpContext);
  }

  async handleReinit(
    session: LeadCtx,
    reinitParameters: ReinitParameters | undefined
  ): Promise<void> {
    if (!reinitParameters) {
      return;
    }
    if (!reinitParameters.enabled) {
      logger.debug("Closer auth: reinit not enabled");
      return;
    }

    const { credentials } = reinitParameters;
    if (!credentials) {
      logger.info("Closer auth: reinit was requested but no previous credentials were available");
      return;
    }
    if (credentials.apiKey === session.apiKey) {
      logger.info(
        `Closer auth: reinit was requested but user has logged in on the same account ${session.id}`
      );
      return;
    }
    logger.debug(`Closer auth: reiniting user ${credentials.id} to ${session.id}`);
    return this.spinnerService
      .reinitLead(session.id, session.apiKey, credentials.apiKey)
      .catch((e) => {
        logger.error({ message: "Couldn't reinit lead", error: e });
        return Promise.reject(AuthErrors.GeneralAuthError);
      });
  }

  async signUpGuest(signUpContext?: InitialContext): Promise<LeadCtx> {
    logger.debug("Closer auth: signing up as new guest");

    const leadCtx = await this.spinnerService.signUpGuest(signUpContext).catch((e) => {
      logger.error({ message: "Couldn't sign up as a guest", error: e });
      return Promise.reject(AuthErrors.GeneralAuthError);
    });
    this.saveGuestSession(leadCtx);
    return leadCtx;
  }

  async signInWithOAuth(loginHintToken: string): Promise<LeadCtx> {
    const leadCtx = await this.spinnerService.signInWithOAuth(loginHintToken);
    this.saveGuestSession(leadCtx);
    return leadCtx;
  }

  continueConversation(token: string) {
    return this.spinnerService.continueConversation(token).then((leadCtx) => {
      this.saveGuestSession(leadCtx);
      return leadCtx;
    });
  }

  joinMeeting(token: string) {
    return this.spinnerService.joinMeeting(token).then((leadCtx) => {
      this.saveGuestSession(leadCtx);
      return leadCtx;
    });
  }

  cancelMeeting(token: string) {
    return this.spinnerService.cancelMeeting(token).then((leadCtx) => {
      this.saveGuestSession(leadCtx);
      return leadCtx;
    });
  }

  setApiKey(apiKey: string) {
    if (this.safeLocalStorage.isSupported()) {
      this.safeLocalStorage.setItem(
        this.localStorageKey,
        JSON.stringify({
          apiKey
        })
      );
    } else {
      this.hostWindowRequests.storageSetItem({
        key: this.localStorageKey,
        value: JSON.stringify({
          apiKey
        }),
      });
    }
  }

  forgetCurrentGuest() {
    if (this.safeLocalStorage.isSupported()) {
      this.safeLocalStorage.removeItem(this.localStorageKey);
    } else {
      this.hostWindowRequests.storageRemoveItem(this.localStorageKey);
    }
    this.spinnerService.setApiKey(undefined);
    fileService.setApiKey(undefined);
  }

  private saveGuestSession<T extends LeadCtx | CustomerCtx>({ apiKey, id }: T): void {
    this.spinnerService.setApiKey(apiKey);
    fileService.setApiKey(apiKey);

    if (this.safeLocalStorage.isSupported()) {
      this.safeLocalStorage.setItem(
        this.localStorageKey,
        serializeStorageCredentials({ apiKey, id })
      );
    } else {
      this.hostWindowRequests.storageSetItem({
        key: this.localStorageKey,
        value: serializeStorageCredentials({ apiKey, id })
      });
    }
  }

  async getStoredCredentials(): Promise<Credentials | undefined> {
    const json = (this.safeLocalStorage.isSupported()
      ? this.safeLocalStorage.getItem(this.localStorageKey)
      : await this.hostWindowRequests.storageGetItem(this.localStorageKey)) || "{}";
    return deserializeStoredCredentials(json);
  }
}

const getLocalStorageCredentialsKey = (orgId: string) =>
  staticConfig.localStorageKeyPrefix + ":" + orgId + ":credentials";

const deserializeStoredCredentials = (json: string): Credentials | undefined => {
  try {
    const storedCredentials = JSON.parse(json);
    if (storedCredentials !== null && typeof storedCredentials === "object" && typeof storedCredentials.apiKey === "string") {
      return storedCredentials;
    }
  } catch (e) {
    noop();
  }
  return;
};

const serializeStorageCredentials = ({ id, apiKey }: Credentials) => {
  return JSON.stringify({
    id,
    apiKey
  });
};
